/**
 * Erzeugt ein Bild aus Bild- und Text-Elementen.
 *
 * @param {number} width  - Breite des Bildes in Pixeln
 * @param {number} height - Höhe des Bildes in Pixeln
 * @param {Array<Object>} elements - Elemente, werden in Reihenfolge gezeichnet (später = weiter oben)
 *
 *   Bild:  { type: "image", src, x, y, width, height }
 *          src: URL, Data-URL, File/Blob oder HTMLImageElement
 *
 *   Text:  { type: "text", text, x, y, fontSize, bold,
 *            color?, fontFamily? }
 *          x/y = linke obere Ecke des Textes, "\n" erzeugt Zeilenumbrüche
 *
 * @param {Object} [options]
 * @param {string} [options.background] - Hintergrundfarbe, z.B. "#ffffff" (Standard: transparent)
 * @param {string} [options.mimeType]   - "image/png" (Standard), "image/jpeg" oder "image/webp"
 * @param {number} [options.quality]    - 0..1, nur für jpeg/webp
 *
 * @returns {Promise<{ url: string, blob: Blob, download: (filename?: string) => void }>}
 */
export async function generateImage(width, height, elements, options = {}) {
   const { background = null, mimeType = "image/png", quality = 0.92 } = options;
   const defaultFont = getComputedStyle(document.body).fontFamily;

   const canvas = document.createElement("canvas");
   canvas.width = width;
   canvas.height = height;
   const ctx = canvas.getContext("2d");

   if (background) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);
   }

   // Alle Bilder vorab parallel laden, dann in richtiger Reihenfolge zeichnen
   const loaded = await Promise.all(
      elements.map((el) => (["image-top", "image-center"].includes(el.type) ? loadImage(el.src) : null)),
   );

   elements.forEach((el, i) => {
      if (el.type === "image-top") {
         const img = loaded[i];

         const iw = img.naturalWidth;
         const ih = img.naturalHeight;

         const dx = el.x ?? 0;
         const dy = el.y ?? 0;
         const dw = el.width ?? iw;
         const dh = el.height ?? ih;

         const scale = Math.max(dw / iw);
         const sw = dw / scale;
         const sh = dh / scale;
         const sx = (iw - sw) / 2;
         const sy = (ih - sh) / 2;

         ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);

         // ctx.drawImage(img, el.x ?? 0, el.y ?? 0, el.width ?? img.naturalWidth, el.height ?? img.naturalHeight);
      } else if (el.type === "image-center") {
         const img = loaded[i];
         const iw = img.naturalWidth;
         const ih = img.naturalHeight;

         const dx = el.x ?? 0;
         const dy = el.y ?? 0;
         const dw = el.width ?? iw;
         const dh = el.height ?? ih;

         const scale = Math.max(dw / iw, dh / ih);
         const sw = dw / scale;
         const sh = dh / scale;
         const sx = (iw - sw) / 2;
         const sy = (ih - sh) / 2;

         ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
      } else if (el.type === "gradient") {
         const color = el.color ?? "#444749";
         const gx = el.x ?? 0;
         const gw = el.width ?? width;
         const gy = el.y ?? 0;
         const gh = el.height ?? 100;

         const grad = ctx.createLinearGradient(0, gy, 0, gy + gh);
         grad.addColorStop(0, hexToRgba(color, 0));
         grad.addColorStop(1, hexToRgba(color, 1));
         ctx.fillStyle = grad;
         ctx.fillRect(gx, gy, gw, gh);

         ctx.fillStyle = color;
         ctx.fillRect(gx, gy + gh, gw, height - (gy + gh));
      } else if (el.type === "text") {
         const fontSize = el.fontSize ?? 16;
         const weight = el.bold ? "bold" : "normal";
         const family = el.fontFamily ?? defaultFont; //"sans-serif";

         ctx.font = `${weight} ${fontSize}px ${family}`;
         ctx.fillStyle = el.color ?? "#000000";
         ctx.textBaseline = "top";
         ctx.textAlign = "left";

         const lineHeight = fontSize * 1.2;
         String(el.text ?? "")
            .split("\n")
            .forEach((line, n) => {
               ctx.fillText(line, el.x ?? 0, (el.y ?? 0) + n * lineHeight);
            });
      }
   });

   const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Bild konnte nicht erzeugt werden"))), mimeType, quality),
   );
   const url = URL.createObjectURL(blob);

   const download = (filename) => {
      const ext = mimeType.split("/")[1].replace("jpeg", "jpg");
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? `bild.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
   };

   return { url, blob, download };
}

/** Lädt ein Bild aus URL, Data-URL, File/Blob oder HTMLImageElement. */
function loadImage(src) {
   if (src instanceof HTMLImageElement && src.complete) return Promise.resolve(src);

   return new Promise((resolve, reject) => {
      const img = new Image();
      // Nötig, damit fremde Bilder den Canvas nicht "verunreinigen"
      // (der Server muss dafür CORS-Header senden)
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Bild konnte nicht geladen werden: ${src}`));
      img.src = src instanceof Blob ? URL.createObjectURL(src) : (src.src ?? src);
   });
}

function hexToRgba(hex, alpha) {
   let h = hex.replace("#", "");
   if (h.length === 3)
      h = h
         .split("")
         .map((c) => c + c)
         .join("");
   const n = parseInt(h, 16);
   return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/* ---------------------------------------------------------------
   Beispiel:

   <img id="vorschau">
   <button id="dl">Herunterladen</button>

   const result = await createImage(800, 600, [
     { type: "image", src: "hintergrund.jpg", x: 0, y: 0, width: 800, height: 600 },
     { type: "image", src: "logo.png", x: 20, y: 20, width: 120, height: 120 },
     { type: "text", text: "Hallo Welt", x: 160, y: 40, fontSize: 48, bold: true },
     { type: "text", text: "Zweite Zeile\nmit Umbruch", x: 160, y: 110, fontSize: 24, bold: false, color: "#444" },
   ], { background: "#ffffff" });

   document.getElementById("vorschau").src = result.url;
   document.getElementById("dl").onclick = () => result.download("mein-bild.png");
---------------------------------------------------------------- */
